/**
 * Service for building analytics event data.
 * Pure functions - no React or PostHog dependencies.
 */

import type { SymbolKey } from '@/entities/symbol';
import type { ExportItemType } from '@/features/DataHub/types/pastExports';

import { DateRangeType } from '../types/context.types';

import type { ExportSettingsType } from '../types/context.types';
import type { ExportOption } from '../types/exportConfig.types';

export const ANALYTICS_EVENTS = {
  EXPORT_BUTTON_CLICK: 'export_download_button_click',
  ENABLE_ACCESS_CLICK: 'datahub_enable_access_button_click',
  PAST_EXPORT_REGENERATED: 'past_export_regenerated',
  PAST_EXPORT_DELETED: 'past_export_deleted',
  CSV_WIDGET_EXPORT_STARTED: 'trade_export_started',
  CSV_WIDGET_EXPORT_COMPLETED: 'trade_export_completed',
  CSV_WIDGET_EXPORT_FAILED: 'trade_export_failed',
  CSV_WIDGET_EXPORT_REGENERATED: 'trade_export_regenerated'
} as const;

export type AnalyticsEvent = {
  eventName: string;
  data: Record<string, unknown>;
};

export type ExportEventParams = {
  exportOption: ExportOption | null;
  symbols: SymbolKey[];
  exportSettings: ExportSettingsType;
};

/**
 * Checks if the export option is an "Enable access" button (for premium features).
 */
export const isEnableAccessButton = (exportOption: ExportOption | null): boolean => {
  return exportOption?.buttonLabel?.toLowerCase() === 'enable access';
};

/**
 * Builds the analytics event data for an "Enable access" button click.
 */
export const buildEnableAccessEventData = (exportOption: ExportOption): AnalyticsEvent => {
  return {
    eventName: ANALYTICS_EVENTS.ENABLE_ACCESS_CLICK,
    data: {
      button: exportOption.id
    }
  };
};

/**
 * Builds the base export event data (common fields).
 */
const buildBaseExportEventData = (
  exportOption: ExportOption | null,
  symbols: SymbolKey[],
  exportSettings: ExportSettingsType
): Record<string, unknown> => {
  return {
    symbols,
    exportName: exportOption?.id,
    dateSettings: exportSettings.dateRangeType,
    includeMetadata: exportSettings.includeMetadata
  };
};

/**
 * Adds date range specific data to the event payload.
 */
const addDateRangeData = (
  baseData: Record<string, unknown>,
  exportSettings: ExportSettingsType
): Record<string, unknown> => {
  if (exportSettings.dateRangeType === DateRangeType.DATES) {
    return {
      ...baseData,
      from: exportSettings.from?.toLocaleString(),
      to: exportSettings.to?.toLocaleString()
    };
  }

  if (exportSettings.dateRangeType === DateRangeType.RANGE) {
    return {
      ...baseData,
      period: exportSettings.period,
      periodNumber: exportSettings.periodNumber
    };
  }

  return baseData;
};

/**
 * Builds the analytics event data for a standard export button click.
 */
export const buildExportEventData = (
  exportOption: ExportOption | null,
  symbols: SymbolKey[],
  exportSettings: ExportSettingsType
): AnalyticsEvent => {
  const baseData = buildBaseExportEventData(exportOption, symbols, exportSettings);
  const data = addDateRangeData(baseData, exportSettings);

  return {
    eventName: ANALYTICS_EVENTS.EXPORT_BUTTON_CLICK,
    data
  };
};

/**
 * Main function to build the appropriate analytics event.
 * Determines whether it's an "Enable access" or standard export event.
 */
export const buildAnalyticsEvent = ({
  exportOption,
  symbols,
  exportSettings
}: ExportEventParams): AnalyticsEvent => {
  if (isEnableAccessButton(exportOption) && exportOption) {
    return buildEnableAccessEventData(exportOption);
  }

  return buildExportEventData(exportOption, symbols, exportSettings);
};

/**
 * Builds event data for past export actions (regenerate / delete).
 */
export const buildPastExportEventData = (
  eventName:
    | typeof ANALYTICS_EVENTS.PAST_EXPORT_REGENERATED
    | typeof ANALYTICS_EVENTS.PAST_EXPORT_DELETED,
  item: ExportItemType
): AnalyticsEvent => {
  return {
    eventName,
    data: {
      id: item.id,
      name: item.name,
      exportType: item.exportType,
      exportSubType: item.exportSubType,
      symbols: (item.symbols ?? []).map(({ key }) => key.code),
      from: item.exportSettings.from,
      to: item.exportSettings.to,
      timezone: item.exportSettings.timezone,
      dateFormat: item.exportSettings.dateFormat,
      includeMetadata: item.exportSettings.includeMetadata
    }
  };
};

type CsvWidgetExportAnalyticsEventName =
  | typeof ANALYTICS_EVENTS.CSV_WIDGET_EXPORT_STARTED
  | typeof ANALYTICS_EVENTS.CSV_WIDGET_EXPORT_COMPLETED
  | typeof ANALYTICS_EVENTS.CSV_WIDGET_EXPORT_FAILED
  | typeof ANALYTICS_EVENTS.CSV_WIDGET_EXPORT_REGENERATED;

type CsvWidgetExportEventParams = {
  exportType: 'trade-csv';
  sourceType: 'query' | 'datalake';
  hasDateFilter: boolean;
};

export const buildCsvWidgetExportEventData = (
  eventName: CsvWidgetExportAnalyticsEventName,
  params: CsvWidgetExportEventParams
): AnalyticsEvent => {
  return {
    eventName,
    data: {
      exportType: params.exportType,
      sourceType: params.sourceType,
      hasDateFilter: params.hasDateFilter
    }
  };
};

export type CopyContentType = 'api' | 'curl' | 'python' | 'powerquery' | 'snowflake';

/**
 * Builds event data for copy-to-clipboard actions, including full export context.
 */
export const buildCopyEventData = (
  contentType: CopyContentType,
  exportOption: ExportOption | null,
  symbols: SymbolKey[],
  exportSettings: ExportSettingsType
): AnalyticsEvent => {
  const baseData = buildBaseExportEventData(exportOption, symbols, exportSettings);
  const data = addDateRangeData(baseData, exportSettings);
  return {
    eventName: 'export_code_copied',
    data: {
      ...data,
      contentType
    }
  };
};
